import Spacer from '@app/components/atoms/Spacer'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { useAxios } from '@common/hooks/useAxios'
import { useState, useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import * as S from './styled'
import { DropDownMenu } from '@app/ui/dropdown-menu/DropDownMenu'
import firebase from 'firebase'
import {
  EvaluationDoc,
  SourceCourse,
} from '../../../pages/CompositeCoursePage/CompositeCoursePage'
import { RatingTemplate } from '../../../types/Templates'
import { useSnackbar } from '@app/snackbar'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import Spinner from '@app/evaluation/components/Spinner'
import DateTimeView from '../../atoms/DateTimeView'
import { MiClub } from '@app/evaluation-core/types'

interface Props {
  courses?: SourceCourse[]
  template?: RatingTemplate
  club: MiClub
}

const MixedEvaluationModal = (props: Props) => {
  const generateAxios = useAxios('/ratings/mixed/generate')
  const dispatch = useDispatch<any>()
  const [isGenerating, setIsGenerating] = useState(false)
  const [openSnackbar] = useSnackbar()
  const [sources, setSources] = useState(
    props.courses?.map(() => ({
      evaluationId: '',
      evaluationName: '',
      ratingDate: '',
    })) || []
  )
  const [clubName, setClubName] = useState(props.club?.name)
  const [courseName, setCourseName] = useState(props.template?.name)
  const [title, setTitle] = useState('')
  const [ratingName, setRatingName] = useState('')
  const [formRatingDate, setFormRatingDate] = useState('')

  const updateSource = (courseIndex: number, evaluation: EvaluationDoc) => {
    console.log({ evaluation })
    setSources((prevSources) => {
      const newSources = [...prevSources]
      newSources[courseIndex] = {
        evaluationId: evaluation.id,
        evaluationName: evaluation.saveName || '',
        ratingDate: evaluation.ratingRevision.ratingDate || '',
      }
      return newSources
    })
  }

  const hideModal = () => dispatch({ type: 'SET_MODAL', params: null })

  const getHoleInfo = (holeId: string, index: number) => {
    const course = props.courses?.find((course) =>
      course.course.holes.some((hole) => hole.physicalID === holeId)
    )
    const hole = course?.course.holes.find((hole) => hole.physicalID === holeId)
    return { course, hole: { ...hole, source: index } }
  }

  const holes = useMemo(
    () => props.template?.holeIds.map(getHoleInfo),
    [props.template]
  )

  const holeEvals = useMemo(() => {
    const holeEvals = holes?.map((hole) => {
      return {
        holeId: hole.hole.physicalID,
        evaluationId: hole.course?.source
          ? sources[hole.course.source - 1].evaluationId
          : undefined,
      }
    })
    return holeEvals
  }, [holes, sources])

  useEffect(() => {
    if (sources.length === 1 && !formRatingDate)
      setFormRatingDate(sources[0].ratingDate)
  }, [sources])

  const submit = async () => {
    try {
      if (sources.length === 0) return

      if (props.template?.name === undefined || props.template?.name === '') {
        openSnackbar(
          'Composite course does not have a name. Please provide one'
        )
        return
      }

      setIsGenerating(true)
      const { data } = await generateAxios.post(`/`, {
        clubId: props.club?.id,
        holes: holeEvals,
        name: props.template?.name,
        templateId: props.template?.id,
        clubNameOverride: clubName,
        courseNameOverride: courseName,
        ratingNameOverride: ratingName,
        dateOverride: formRatingDate,
        title: title,
      })
      const downloadFolder = (ref: string) => {
        const doc = firebase.storage().ref(ref)
        doc.getDownloadURL().then((url) => {
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = document.title || ''
          anchor.click()
        })
      }

      downloadFolder(data)
      hideModal()
    } catch (error) {
      console.error('Error generating form:', error)
      openSnackbar('Error generating form')
    } finally {
      setIsGenerating(false)
    }
  }

  const CourseDropdown = ({
    course,
    index,
  }: {
    course: SourceCourse
    index: number
  }) => (
    <div key={'source' + course.source} style={{ marginRight: 8, width: 300 }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div>
          <span>
            <span style={{ fontWeight: 500 }}>
              Select rating {course.source}
            </span>{' '}
            <span style={{ fontWeight: 600 }}>{course.course.name}</span>
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 400, color: '#000' }}>
          Required
        </span>
      </div>
      <Spacer height={12} />
      <DropDownMenu
        yAdj={10}
        width={280}
        renderValue={() => (
          <span style={{ display: 'flex', width: '100%' }}>
            <span style={{ flexGrow: 1, textAlign: 'start' }}>
              {sources[index]?.evaluationId === ''
                ? 'Select Rating'
                : sources[index]?.evaluationName &&
                  sources[index].evaluationName.length > 0
                ? sources[index].evaluationName
                : 'Unnamed'}
            </span>
            <DateTimeView
              dateString={sources[index].ratingDate}
              showTime={false}
            />
          </span>
        )}
        relative
      >
        {course.evaluations.length ? (
          course.evaluations.map((evaluation) => (
            <S.Source
              key={`evaluation-${evaluation.id}`}
              onClick={() => updateSource(index, evaluation)}
            >
              <span style={{ flexGrow: 1, textAlign: 'start' }}>
                {evaluation.saveName === '' ? 'Unnamed' : evaluation.saveName}
              </span>
              <DateTimeView
                dateString={evaluation?.ratingRevision.ratingDate}
                showTime={false}
              />
            </S.Source>
          ))
        ) : (
          <S.Source>No Ratings available</S.Source>
        )}
      </DropDownMenu>
    </div>
  )

  const RatingDateDropdown = () => {
    const isDisabled =
      sources.filter((source) => source.evaluationId === '').length > 0 ||
      sources.length <= 1
    return (
      <div
        style={{
          marginRight: 8,
          width: 300,
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <Spacer height={8} />
        <div
          style={{ display: 'flex', flexDirection: 'column', fontWeight: 500 }}
        >
          <span>Rating date:</span>
        </div>
        <Spacer height={8} />
        <DropDownMenu
          disabledState={{
            disabled: isDisabled,
            message: 'Select Rating First',
          }}
          yAdj={10}
          width={280}
          value={
            formRatingDate !== ''
              ? new Date(formRatingDate).toLocaleDateString('sv-SE')
              : 'Select Date'
          }
          relative
        >
          {sources.length &&
            sources.map((source, i) => (
              <S.Source
                key={`source-${i}`}
                onClick={() => setFormRatingDate(source.ratingDate)}
              >
                <span style={{ flexGrow: 1, textAlign: 'start' }}>
                  {source.evaluationName}
                </span>
                <DateTimeView dateString={source.ratingDate} showTime={false} />
              </S.Source>
            ))}
        </DropDownMenu>
      </div>
    )
  }

  return (
    <S.Wrapper>
      <S.Header>
        <S.Title>Generate Form</S.Title>
        <S.CloseButton onClick={hideModal}>X</S.CloseButton>
      </S.Header>
      <S.Line />

      {isGenerating ? (
        <S.SpinnerContainer>
          <Spinner />
          <S.SpinnerText> Generating...</S.SpinnerText>
        </S.SpinnerContainer>
      ) : (
        <S.Content>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
            <div
              style={{
                width: 280,
              }}
            >
              <label htmlFor="name" style={{ fontWeight: 500 }}>
                Title
              </label>
              <div style={{ fontSize: 12 }}>
                Will NOT appear in forms header.{' '}
                <span style={{ color: '#212121' }}>Required</span>
              </div>
              <S.Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div
              style={{
                width: 280,
              }}
            >
              <label htmlFor="name" style={{ fontWeight: 500 }}>
                Club name
              </label>
              <div style={{ fontSize: 12 }}>
                Will appear in forms header.{' '}
                <span style={{ color: '#212121' }}>Required</span>
              </div>
              <S.Input
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
              />
            </div>

            <div
              style={{
                width: 280,
              }}
            >
              <label htmlFor="name" style={{ fontWeight: 500 }}>
                Composite Course name
              </label>
              <div style={{ fontSize: 12 }}>
                Will appear in forms header.{' '}
                <span style={{ color: '#212121' }}>Required</span>
              </div>
              <S.Input
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
              />
            </div>
          </div>
          <Spacer height={8} />
          <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
            <div
              style={{
                width: 280,
              }}
            >
              <label htmlFor="name" style={{ fontWeight: 500 }}>
                Rating name
              </label>
              <div style={{ fontSize: 12 }}>
                Will appear in forms header.{' '}
                <span style={{ color: '#212121' }}>Required</span>
              </div>
              <S.Input
                value={ratingName}
                onChange={(e) => setRatingName(e.target.value)}
              />
            </div>

            <Spacer height={8} />

            <div style={{ display: 'flex' }}>
              {props.courses?.map((course, index) => (
                <CourseDropdown
                  key={`course-${course.course.id}`}
                  course={course}
                  index={index}
                />
              ))}
            </div>
          </div>
          <div>
            {true && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <RatingDateDropdown />
              </div>
            )}
          </div>
          <Spacer height={8} />
          <div style={{ fontWeight: 500 }}>Holes</div>
          <S.HoleGrid>
            {holes?.map((hole, holeIndex) => (
              <S.RatingHole key={`hole-${hole.hole.id}`} selected={false}>
                <S.RatingHoleHeader>
                  <S.RatingHoleTitle>
                    Hole:{' '}
                    <span style={{ fontWeight: 800 }}>{holeIndex + 1}</span>{' '}
                    par: {hole.hole.par}
                  </S.RatingHoleTitle>
                  {holeEvals?.[holeIndex]?.evaluationId ? (
                    <S.CheckContainer>
                      <FeatherIcon name="check" size={18} color="white" />
                    </S.CheckContainer>
                  ) : null}
                </S.RatingHoleHeader>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '2px 16px 8px 16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <S.RatingHoleHoleInfoText>
                      {hole.course?.course.name}{' '}
                      <span style={{ fontWeight: 800 }}>
                        {hole.hole.courseHoleNumber}
                      </span>
                    </S.RatingHoleHoleInfoText>
                  </div>
                </div>
              </S.RatingHole>
            ))}
          </S.HoleGrid>
          <RoundedButton
            disabled={!holeEvals || holeEvals.some((h) => !h.evaluationId)}
            title="Generate forms"
            width={'100%'}
            onPress={submit}
          />
        </S.Content>
      )}
    </S.Wrapper>
  )
}

export default MixedEvaluationModal
